<template>
    <div>
        <slot />
    </div>
</template>

<script setup>
    import { noop } from '@vueuse/core';
    import { Color, EquirectangularReflectionMapping, Mesh, Vector2 } from 'three';
    import { inject, onMounted, ref, watch, watchEffect } from 'vue';

    import { randomInt } from '@resn/gozer-math';
    import { basePaneOptions, usePane, useResize } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import SlabMaterial from '~/components/gl/materials/SlabMaterial';
    import { useBloom } from '~/composables/useBloom';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const props = defineProps({
        grader: { defaut: 'psa' },
        object: { defaut: null },
        active: { defaut: false },
        preRevealSticker: { defaut: '' },
        colorPalette: { defaut: [] },
    });

    const { inProgress, revealPr, heartbeatPr, wipeAngle, lightAngle, depthOffset, time } =
        inject('card');

    const visible = ref(true);

    const uRes = new Vector2();
    useResize(({ width, height }) => {
        uRes.set(width, height);
    }, true);

    const color = ref('white');
    const ior = ref(0);
    // const dispersionAmount = ref(0.04); // for vUv coord
    const dispersionAmount = ref(0.002); // for screen space coord

    const outlineScale = {
        psa: 0.83,
        bgs: 0.8,
        cgc: 0.83,
        sgc: 0.83,
    };

    const colors = [
        ['#87b2ff', '#bd8ef6'],
        ['#ffd987', '#b96954'],
        ['#68a4ff', '#54b996'],
        ['#68ffd1', '#92fc70'],
    ];
    const preRevealColor1 = ref('#000');
    const preRevealColor2 = ref('#000');

    const bloom = useBloom();

    onMounted(() => {
        setSticker(props.preRevealSticker);
    });

    const material = new SlabMaterial({
        uniforms: {
            uTime: 0,
            uAlpha: 1,
            uInProgress: 0,
            uIor: ior.value,
            uAlphaMask: null,
            uNoise: null,
            tMatcap: null,
            uEnvMap: null,
            uOutlineScale: 0,
            uTexPreRevealSticker: null,
            uPreRevealStickerAspect: 1,
            uColorPalette: props.colorPalette,
            uDispersionAmount: dispersionAmount.value,
            uMatcapRotation: lightAngle.value,

            uPreRevealColor1: new Color(preRevealColor1.value),
            uPreRevealColor2: new Color(preRevealColor2.value),

            uDepthOffset: 0,
            uHeartbeatPr: 0,

            uLightsDownPr: 0,
            uRes,
        },
        options: {
            flipUV: false,
        },
    });

    let obj;

    const loader = useGlobalAssets(({ data }) => {
        data.matcapDiffuse.flipY = true;
        data.envMap.mapping = EquirectangularReflectionMapping;

        material.uniforms.uAlphaMask.value = data[`card-mask-${props.grader}`];
        material.uniforms.tMatcap.value = data.matcapDiffuse;
        material.uniforms.uNoise.value = data.pNoise;
        material.uniforms.uEnvMap.value = data.envMap;
    });

    const setObject = (first = false) => {
        if (obj) obj.parent.remove(obj);

        // assumes slab is always passed in as a prop on creation
        obj = props.object;
        obj.material = material;
        obj.renderOrder = 1;
        window.slab = obj;

        if (loader.assets) {
            const data = loader.assets;
            const cardId = `card-mask-${props.grader}`;
            material.uniforms.uAlphaMask.value = data[cardId];
        }

        material.uniforms.uOutlineScale.value = outlineScale[props.grader];

        if (bloom.pass?.value) bloom.pass.value.selection?.add(obj);

        if (!first) updateColors();
    };

    const updateColors = () => {
        const rColorIndex = randomInt(0, colors.length - 1);
        preRevealColor1.value = colors[rColorIndex][0];
        preRevealColor2.value = colors[rColorIndex][1];

        emit('colorUpdate', preRevealColor1.value, preRevealColor2.value);
    };

    setObject(true);

    const lightsDownPr = ref(0);

    const alphaPr = ref(1);

    let tlLightsDown;
    const playGrailAnimation = ({ revealDelay = 5 } = {}) => {
        tlLightsDown?.kill();
        tlLightsDown = gsap
            .timeline()
            .to(alphaPr, { value: 0, duration: 1 }, 0)
            .to(alphaPr, { value: 1, duration: 0.5 }, revealDelay);

        return tlLightsDown;
    };

    // Function to set texture
    const setSticker = (url) => {
        const { uTexPreRevealSticker, uPreRevealStickerAspect } = material.uniforms;

        if (!url) {
            uTexPreRevealSticker.value?.dispose();
            return;
        }

        loadBitmapTexture(url, { flipY: true })
            .then((texture) => {
                uTexPreRevealSticker.value?.dispose();
                uTexPreRevealSticker.value = texture;
                uPreRevealStickerAspect.value = texture.image.width / texture.image.height;
            })
            .catch(noop);
    };

    const resetTransitionUniforms = () => {
        material.clearTransitionUniforms({ transition: true });
    };

    const setTransitionUniform = (key, value) => {
        material[key] = value;
    };

    watch(() => props.preRevealSticker, setSticker);
    watch(() => props.object, setObject);

    // refs that update less frequently
    watchEffect(() => {
        material.uniforms.uIor.value = ior.value;
        // material.uniforms.uWipeRevealPr.value = revealPr.value;
        material.uniforms.uWipeAngle.value = wipeAngle.value;
        material.uniforms.uDispersionAmount.value = dispersionAmount.value;
        material.uniforms.uPreRevealColor1.value.set(preRevealColor1.value);
        material.uniforms.uPreRevealColor2.value.set(preRevealColor2.value);

        material.uniforms.uInProgress.value = inProgress.value;
        material.uniforms.uAlpha.value = alphaPr.value;

        obj.visible = visible.value;
    });

    watchEffect(() => {
        material.uniforms.uWipeRevealPr.value = revealPr.value;
    });

    // refs that update more frequently
    watchEffect(() => {
        material.uniforms.uDepthOffset.value = depthOffset.value;
        material.uniforms.uLightsDownPr.value = lightsDownPr.value;
    });

    // Per-frame uniforms — updated directly to avoid reactive overhead
    const onUpdate = () => {
        material.uniforms.uTime.value = time.value;
        material.uniforms.uHeartbeatPr.value = heartbeatPr.value;
        material.uniforms.uMatcapRotation.value = lightAngle.value;
    };

    onMounted(() => {
        updateColors();

        window.lightsDownPr = lightsDownPr;
    });

    const reset = () => {
        tlLightsDown?.kill();
        alphaPr.value = 1;
        lightsDownPr.value = 0;
    };

    const emit = defineEmits(['colorUpdate']);

    defineExpose({
        onUpdate,
        playGrailAnimation,
        setTransitionUniform,
        resetTransitionUniforms,
        reset,
    });

    usePane(
        [
            {
                name: 'dispersionAmount',
                value: dispersionAmount,
                options: { min: 0, max: 0.01, step: 0.0001 },
            },
            { name: 'alphaPr', value: alphaPr, options: basePaneOptions },
            { name: 'preRevealColor1', value: preRevealColor1 },
            { name: 'preRevealColor2', value: preRevealColor2 },
            { name: 'visible', value: visible },
            { name: 'color', value: color },
            { name: 'ior', value: ior, options: { min: 0, max: 5, step: 0.01 } },
        ],
        { title: 'Slab', expanded: false }
    );
</script>

<style lang="scss" scoped></style>
